import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { sb } from '../lib/supabase';
import { fmt, priorityBadgeClass } from '../lib/helpers';
import { useSupabaseAuth } from '../context/AuthContext';
import useWorkspaceAccess from '../hooks/useWorkspaceAccess';
import Loader from '../components/Loader';
import Modal from '../components/Modal';
import SlideOver from '../components/SlideOver';
import Tabs from '../components/Tabs';
import TypeBadge, { RAID_TYPES } from '../components/TypeBadge';
import OwnerSearch from '../components/OwnerSearch';
import { summariseRisks, AiUnavailableError } from '../lib/ai';
import { useRexScreen } from '../context/RexContext';

const ALL_TYPES = ['risk', 'assumption', 'issue', 'dependency', 'decision'];
const PRIORITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };
const STATUS_VARIANT = { open: '', in_progress: 'am', closed: 'gr', escalated: 'rd' };

const BLANK_FORM = {
  type: 'risk', title: '', description: '', priority: 'medium', status: 'open',
  owner_id: null, owner_name: '', due_date: '', project_id: '',
  dependency_kind: 'internal', dependency_link: '',
};

export default function RaidLog() {
  const { project, programme, projects } = useOutletContext();
  const { profile } = useSupabaseAuth();
  const { canWrite } = useWorkspaceAccess();
  const navigate = useNavigate();

  const [raid, setRaid] = useState([]);
  const [gateLinks, setGateLinks] = useState([]);     // {id, gate_id, raid_item_id, stage_gates:{...}}
  const [related, setRelated] = useState([]);         // {a_id, b_id}
  const [gates, setGates] = useState([]);             // pickable gates in this programme
  const [loading, setLoading] = useState(true);

  // filters
  const [typeSet, setTypeSet] = useState(() => new Set(ALL_TYPES));
  const [projectSet, setProjectSet] = useState(null); // null => all
  const [statusFilter, setStatusFilter] = useState('active'); // active | all | closed
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [colTitle, setColTitle] = useState('');
  const [colOwner, setColOwner] = useState('');
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });

  // panel + modal
  const [selectedId, setSelectedId] = useState(null);
  const [panelTab, setPanelTab] = useState('details');
  const [updates, setUpdates] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [modal, setModal] = useState(null); // {mode:'add'|'edit', id?}
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [picker, setPicker] = useState(null); // 'gate' | 'related'

  // AI
  const [ai, setAi] = useState(null); // {loading, text, error}

  const projectIds = useMemo(() => (projects || []).map((p) => p.id), [projects]);
  const projectName = useCallback(
    (pid) => (pid ? (projects.find((p) => p.id === pid)?.name || 'Project') : 'Programme-level'),
    [projects]
  );
  const raidById = useMemo(() => {
    const m = {};
    raid.forEach((r) => { m[r.id] = r; });
    return m;
  }, [raid]);

  const load = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const { data: items } = await sb
        .from('raid_items')
        .select('*, profiles(full_name, email)')
        .eq('project_id', project.id)
        .order('created_at', { ascending: false });
      const list = items || [];
      setRaid(list);
      const ids = list.map((r) => r.id);

      const [glRes, relRes, gateRes] = await Promise.all([
        ids.length
          ? sb.from('gate_raid_links').select('id, gate_id, criterion_id, raid_item_id, stage_gates(name, status, project_id)').in('raid_item_id', ids)
          : Promise.resolve({ data: [] }),
        ids.length
          ? sb.from('raid_related_items').select('a_id, b_id').in('a_id', ids)
          : Promise.resolve({ data: [] }),
        projectIds.length
          ? sb.from('stage_gates').select('id, name, status, project_id').in('project_id', projectIds)
          : Promise.resolve({ data: [] }),
      ]);
      setGateLinks(glRes.data || []);
      setRelated(relRes.data || []);
      setGates(gateRes.data || []);
    } catch (e) {
      console.error('[RaidLog] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [project?.id, projectIds]);

  useEffect(() => { load(); }, [load]);

  // Load updates when a panel item is opened
  useEffect(() => {
    if (!selectedId) { setUpdates([]); return; }
    let cancelled = false;
    (async () => {
      const { data } = await sb
        .from('raid_updates')
        .select('id, body, created_at, profiles(full_name)')
        .eq('raid_item_id', selectedId)
        .order('created_at', { ascending: false });
      if (!cancelled) setUpdates(data || []);
    })();
    return () => { cancelled = true; };
  }, [selectedId]);

  // ── derived maps ──
  const gateLinksByItem = useMemo(() => {
    const m = {};
    gateLinks.forEach((l) => { (m[l.raid_item_id] ||= []).push(l); });
    return m;
  }, [gateLinks]);

  const relatedByItem = useMemo(() => {
    const m = {};
    related.forEach((r) => { (m[r.a_id] ||= []).push(r.b_id); });
    return m;
  }, [related]);

  // ── counts for summary bar ──
  const summary = useMemo(() => {
    const open = raid.filter((r) => r.status !== 'closed');
    return {
      open: open.length,
      openRisks: open.filter((r) => r.type === 'risk').length,
      escalated: open.filter((r) => r.status === 'escalated').length,
      openIssues: open.filter((r) => r.type === 'issue').length,
      critical: open.filter((r) => r.priority === 'critical').length,
      closed: raid.filter((r) => r.status === 'closed').length,
    };
  }, [raid]);

  const rexSnapshot = useMemo(() => ({ ...summary, programmeName: programme?.name, projectName: project?.name }), [summary, programme?.name, project?.name]);
  useRexScreen({ snapshot: rexSnapshot });

  // ── filtering + sorting ──
  const visible = useMemo(() => {
    let rows = raid.filter((r) => typeSet.has(r.type));
    if (statusFilter === 'active') rows = rows.filter((r) => r.status !== 'closed');
    else if (statusFilter === 'closed') rows = rows.filter((r) => r.status === 'closed');
    if (priorityFilter !== 'all') rows = rows.filter((r) => r.priority === priorityFilter);
    if (projectSet) rows = rows.filter((r) => projectSet.has(r.project_id || 'programme'));
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((r) => (r.title || '').toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q) || (r.ref || '').toLowerCase().includes(q));
    }
    if (colTitle.trim()) rows = rows.filter((r) => (r.title || '').toLowerCase().includes(colTitle.toLowerCase()));
    if (colOwner.trim()) rows = rows.filter((r) => (r.profiles?.full_name || '').toLowerCase().includes(colOwner.toLowerCase()));

    const dir = sort.dir === 'asc' ? 1 : -1;
    rows = [...rows].sort((a, b) => {
      let av, bv;
      switch (sort.key) {
        case 'priority': av = PRIORITY_RANK[a.priority] ?? 9; bv = PRIORITY_RANK[b.priority] ?? 9; break;
        case 'owner': av = a.profiles?.full_name || ''; bv = b.profiles?.full_name || ''; break;
        case 'due_date': av = a.due_date || ''; bv = b.due_date || ''; break;
        case 'status': av = a.status || ''; bv = b.status || ''; break;
        case 'title': av = a.title || ''; bv = b.title || ''; break;
        case 'ref': av = a.ref || ''; bv = b.ref || ''; break;
        default: av = a.created_at || ''; bv = b.created_at || '';
      }
      return av < bv ? -dir : av > bv ? dir : 0;
    });
    return rows;
  }, [raid, typeSet, statusFilter, priorityFilter, projectSet, search, colTitle, colOwner, sort]);

  // ── actions ──
  function toggleType(t) {
    setTypeSet((prev) => {
      const next = new Set(prev);
      next.has(t) ? next.delete(t) : next.add(t);
      return next.size ? next : new Set(ALL_TYPES);
    });
  }

  function toggleSort(key) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  }

  function openAdd() {
    setForm({ ...BLANK_FORM, project_id: project?.id || '' });
    setError(null);
    setModal({ mode: 'add' });
  }
  function openEdit(item) {
    setForm({
      type: item.type || 'risk', title: item.title || '', description: item.description || '',
      priority: item.priority || 'medium', status: item.status || 'open',
      owner_id: item.owner_id || null, owner_name: item.profiles?.full_name || '',
      due_date: item.due_date ? item.due_date.split('T')[0] : '',
      project_id: item.project_id || '',
      dependency_kind: item.dependency_kind || 'internal', dependency_link: item.dependency_link || '',
    });
    setError(null);
    setModal({ mode: 'edit', id: item.id });
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    const payload = {
      type: form.type, title: form.title.trim(), description: form.description.trim() || null,
      priority: form.priority, status: form.status, owner_id: form.owner_id || null,
      due_date: form.due_date || null, project_id: form.project_id || project?.id || null,
      dependency_kind: form.type === 'dependency' ? form.dependency_kind : null,
      dependency_link: form.type === 'dependency' ? (form.dependency_link.trim() || null) : null,
    };
    const sel = '*, profiles(full_name, email)';
    let res;
    if (modal.mode === 'add') {
      res = await sb.from('raid_items').insert({ ...payload, programme_id: programme.id }).select(sel);
    } else {
      res = await sb.from('raid_items').update(payload).eq('id', modal.id).select(sel);
    }
    if (res.error) {
      setSaving(false);
      setError(res.error.message);
      return;
    }
    if (modal.mode === 'add') setRaid((p) => [res.data[0], ...p]);
    else setRaid((p) => p.map((r) => (r.id === modal.id ? res.data[0] : r)));
    setSaving(false);
    setModal(null);
  }

  async function setStatus(item, status) {
    await sb.from('raid_items').update({ status }).eq('id', item.id);
    setRaid((p) => p.map((r) => (r.id === item.id ? { ...r, status } : r)));
  }

  async function addComment() {
    const body = newComment.trim();
    if (!body || !selectedId) return;
    const { data } = await sb.from('raid_updates')
      .insert({ raid_item_id: selectedId, body, author_id: profile?.id || null })
      .select('id, body, created_at, profiles(full_name)');
    if (data?.[0]) setUpdates((u) => [data[0], ...u]);
    setNewComment('');
  }

  async function linkGate(gateId) {
    const { data } = await sb.from('gate_raid_links')
      .insert({ gate_id: gateId, raid_item_id: selectedId, created_by: profile?.id || null })
      .select('id, gate_id, criterion_id, raid_item_id, stage_gates(name, status, project_id)');
    if (data?.[0]) setGateLinks((g) => [...g, data[0]]);
    setPicker(null);
  }
  async function unlinkGate(linkId) {
    await sb.from('gate_raid_links').delete().eq('id', linkId);
    setGateLinks((g) => g.filter((l) => l.id !== linkId));
  }

  async function linkRelated(otherId) {
    await sb.from('raid_related_items').insert({ a_id: selectedId, b_id: otherId });
    // DB trigger mirrors (b,a); reflect both directions locally
    setRelated((r) => [...r, { a_id: selectedId, b_id: otherId }, { a_id: otherId, b_id: selectedId }]);
    setPicker(null);
  }
  async function unlinkRelated(otherId) {
    await sb.from('raid_related_items').delete().eq('a_id', selectedId).eq('b_id', otherId);
    await sb.from('raid_related_items').delete().eq('a_id', otherId).eq('b_id', selectedId);
    setRelated((r) => r.filter((x) => !((x.a_id === selectedId && x.b_id === otherId) || (x.a_id === otherId && x.b_id === selectedId))));
  }

  async function runAiSummary() {
    setAi({ loading: true });
    const openRisks = raid
      .filter((r) => r.type === 'risk' && r.status !== 'closed')
      .map((r) => ({ ref: r.ref, title: r.title, description: r.description, priority: r.priority, status: r.status, owner: r.profiles?.full_name, due: r.due_date }));
    try {
      const text = await summariseRisks({ programmeName: programme.name, kind: 'risks', items: openRisks });
      setAi({ text });
    } catch (e) {
      if (e instanceof AiUnavailableError) {
        setAi({ error: 'AI summary isn\u2019t available yet — the ai-summarise Edge Function needs to be deployed with an ANTHROPIC_API_KEY secret.' });
      } else {
        setAi({ error: e?.message || 'Could not generate summary.' });
      }
    }
  }

  const selected = selectedId ? raidById[selectedId] : null;

  const projectFilterButtons = [{ id: 'programme', name: 'Programme-level' }, ...(projects || [])];

  return (
    <div className="content">
      {/* Summary bar */}
      <div className="m4" style={{ gridTemplateColumns: 'repeat(5, minmax(0,1fr))' }}>
        <div className="met"><div className="mlbl">Open items</div><div className="mval">{summary.open}</div><div className="msub">excl. closed</div></div>
        <div className="met"><div className="mlbl">Open risks</div><div className="mval" style={{ color: summary.openRisks ? '#A32D2D' : undefined }}>{summary.openRisks}</div><div className="msub">{summary.escalated} escalated</div></div>
        <div className="met"><div className="mlbl">Open issues</div><div className="mval" style={{ color: summary.openIssues ? '#A32D2D' : undefined }}>{summary.openIssues}</div><div className="msub">being worked</div></div>
        <div className="met"><div className="mlbl">Critical</div><div className="mval" style={{ color: summary.critical ? '#A32D2D' : undefined }}>{summary.critical}</div><div className="msub">priority</div></div>
        <div className="met"><div className="mlbl">Closed</div><div className="mval">{summary.closed}</div><div className="msub">resolved</div></div>
      </div>

      <div className="card">
        <div className="chdr">
          <span className="ctitle">RAID log — {programme.name}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-ghost" onClick={runAiSummary}>✦ Summarise open risks</button>
            {canWrite && <button className="btn-primary" onClick={openAdd}>+ Add item</button>}
          </div>
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
          <div className="filter-tabs">
            {ALL_TYPES.map((t) => (
              <button key={t} className={`ftab${typeSet.has(t) ? ' active' : ''}`} onClick={() => toggleType(t)}>
                {RAID_TYPES[t].label}s
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="filter-tabs" style={{ marginBottom: 0 }}>
              {['active', 'all', 'closed'].map((s) => (
                <button key={s} className={`ftab${statusFilter === s ? ' active' : ''}`} onClick={() => setStatusFilter(s)}>
                  {s[0].toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <select className="form-select" style={{ width: 'auto' }} value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
              <option value="all">All priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <input className="form-input" style={{ width: 200 }} placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {projects?.length > 0 && (
            <div className="filter-tabs" style={{ marginBottom: 0 }}>
              {projectFilterButtons.map((p) => {
                const on = !projectSet || projectSet.has(p.id);
                return (
                  <button key={p.id} className={`ftab${on ? ' active' : ''}`} onClick={() => {
                    setProjectSet((prev) => {
                      const base = prev ? new Set(prev) : new Set(projectFilterButtons.map((x) => x.id));
                      base.has(p.id) ? base.delete(p.id) : base.add(p.id);
                      return base.size === projectFilterButtons.length ? null : base;
                    });
                  }}>{p.name}</button>
                );
              })}
            </div>
          )}
        </div>

        {/* Table */}
        {loading ? <Loader /> : (
          <RaidTable
            rows={visible}
            sort={sort}
            onSort={toggleSort}
            colTitle={colTitle} setColTitle={setColTitle}
            colOwner={colOwner} setColOwner={setColOwner}
            projectName={projectName}
            gateLinksByItem={gateLinksByItem}
            onOpen={(r) => { setSelectedId(r.id); setPanelTab('details'); }}
          />
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <SlideOver
          onClose={() => setSelectedId(null)}
          header={
            <div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                <TypeBadge type={selected.type} />
                {selected.ref && <span style={{ fontSize: 11, color: '#888780' }}>{selected.ref}</span>}
              </div>
              <div style={{ fontSize: 15, fontWeight: 500, color: '#2c2c2a' }}>{selected.title}</div>
            </div>
          }
        >
          <Tabs tabs={[{ id: 'details', label: 'Details' }, { id: 'updates', label: `Updates (${updates.length})` }, { id: 'links', label: 'Links' }]} active={panelTab} onChange={setPanelTab} />
          <div style={{ paddingTop: 14 }}>
            {panelTab === 'details' && (
              <DetailsTab item={selected} projectName={projectName} canWrite={canWrite} onEdit={() => openEdit(selected)} onStatus={(s) => setStatus(selected, s)} />
            )}
            {panelTab === 'updates' && (
              <UpdatesTab updates={updates} value={newComment} onChange={setNewComment} onAdd={addComment} canWrite={canWrite} />
            )}
            {panelTab === 'links' && (
              <LinksTab
                item={selected}
                gateLinks={gateLinksByItem[selected.id] || []}
                relatedIds={relatedByItem[selected.id] || []}
                raidById={raidById}
                projectName={projectName}
                canWrite={canWrite}
                onUnlinkGate={unlinkGate}
                onUnlinkRelated={unlinkRelated}
                onOpenGatePicker={() => setPicker('gate')}
                onOpenRelatedPicker={() => setPicker('related')}
                onOpenRelated={(rid) => { setSelectedId(rid); setPanelTab('links'); }}
                navigate={navigate}
                programmeId={programme.id}
              />
            )}
          </div>
        </SlideOver>
      )}

      {/* Add / edit modal */}
      {modal && (
        <Modal
          title={modal.mode === 'add' ? 'Add RAID item' : 'Edit RAID item'}
          onClose={() => !saving && setModal(null)}
          busy={saving}
          footer={
            <>
              <button className="btn-ghost" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving || !form.title.trim()}>
                {saving ? 'Saving…' : modal.mode === 'add' ? 'Add item' : 'Save changes'}
              </button>
            </>
          }
        >
          <RaidForm form={form} setForm={setForm} projects={projects} error={error} />
        </Modal>
      )}

      {/* Gate picker */}
      {picker === 'gate' && selected && (
        <PickerModal
          title="Link a gate"
          onClose={() => setPicker(null)}
          options={gates
            .filter((g) => !(gateLinksByItem[selected.id] || []).some((l) => l.gate_id === g.id))
            .map((g) => ({ id: g.id, label: g.name, sub: `${projectName(g.project_id)} · ${g.status?.replace(/_/g, ' ')}` }))}
          onPick={linkGate}
          empty="No gates available to link. Create gates in Stage gates first."
        />
      )}

      {/* Related item picker */}
      {picker === 'related' && selected && (
        <PickerModal
          title="Link a related RAID item"
          onClose={() => setPicker(null)}
          options={raid
            .filter((r) => r.id !== selected.id && !(relatedByItem[selected.id] || []).includes(r.id))
            .map((r) => ({ id: r.id, label: r.title, sub: `${RAID_TYPES[r.type]?.label || r.type} · ${projectName(r.project_id)}` }))}
          onPick={linkRelated}
          empty="No other RAID items to link."
        />
      )}

      {/* AI summary modal */}
      {ai && (
        <Modal
          title="Open risks — AI briefing"
          onClose={() => setAi(null)}
          footer={
            <>
              {ai.text && <button className="btn-ghost" onClick={() => navigator.clipboard?.writeText(ai.text)}>Copy</button>}
              <button className="btn-primary" onClick={() => setAi(null)}>Close</button>
            </>
          }
        >
          {ai.loading && <Loader text="Generating briefing…" />}
          {ai.error && <div className="form-error" style={{ margin: 0 }}>{ai.error}</div>}
          {ai.text && <div style={{ fontSize: 12.5, color: '#2c2c2a', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{ai.text}</div>}
        </Modal>
      )}
    </div>
  );
}

// ── Table ──────────────────────────────────────────────────────────────────────
function SortHead({ label, k, sort, onSort, style }) {
  const arrow = sort.key === k ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '';
  return (
    <div onClick={() => onSort(k)} style={{ cursor: 'pointer', ...style }}>{label}{arrow}</div>
  );
}

function RaidTable({ rows, sort, onSort, colTitle, setColTitle, colOwner, setColOwner, projectName, gateLinksByItem, onOpen }) {
  const grid = '70px 90px 1fr 130px 90px 110px 100px 70px';
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 8, padding: '4px 8px', fontSize: 10, color: '#888780', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '0.5px solid #f1efe8' }}>
        <SortHead label="Ref" k="ref" sort={sort} onSort={onSort} />
        <div>Type</div>
        <SortHead label="Title" k="title" sort={sort} onSort={onSort} />
        <SortHead label="Owner" k="owner" sort={sort} onSort={onSort} />
        <SortHead label="Priority" k="priority" sort={sort} onSort={onSort} />
        <SortHead label="Status" k="status" sort={sort} onSort={onSort} />
        <SortHead label="Due" k="due_date" sort={sort} onSort={onSort} />
        <div>Gates</div>
      </div>
      {/* column filter inputs */}
      <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 8, padding: '4px 8px 8px' }}>
        <div /><div />
        <input className="form-input" style={{ fontSize: 11, padding: '4px 7px' }} placeholder="Filter title" value={colTitle} onChange={(e) => setColTitle(e.target.value)} />
        <input className="form-input" style={{ fontSize: 11, padding: '4px 7px' }} placeholder="Filter owner" value={colOwner} onChange={(e) => setColOwner(e.target.value)} />
        <div /><div /><div /><div />
      </div>
      {rows.length === 0 ? (
        <div className="empty">No RAID items match the current filters.</div>
      ) : rows.map((r) => {
        const links = gateLinksByItem[r.id] || [];
        return (
          <div
            key={r.id}
            onClick={() => onOpen(r)}
            style={{ display: 'grid', gridTemplateColumns: grid, gap: 8, padding: '9px 8px', alignItems: 'center', borderBottom: '0.5px solid #f1efe8', cursor: 'pointer', fontSize: 12, opacity: r.status === 'closed' ? 0.6 : 1 }}
          >
            <div style={{ fontSize: 11, color: '#888780' }}>{r.ref || '—'}</div>
            <div><TypeBadge type={r.type} short /></div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: '#2c2c2a', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</div>
              <div style={{ fontSize: 10, color: '#888780' }}>{projectName(r.project_id)}</div>
            </div>
            <div style={{ color: '#5f5e5a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.profiles?.full_name || '—'}</div>
            <div><span className={`b ${priorityBadgeClass(r.priority)}`}>{r.priority}</span></div>
            <div><span className={`b ${STATUS_VARIANT[r.status] || ''}`}>{(r.status || 'open').replace(/_/g, ' ')}</span></div>
            <div style={{ color: '#5f5e5a' }}>{r.due_date ? fmt(r.due_date) : '—'}</div>
            <div style={{ color: '#5f5e5a' }}>{links.length ? `🔗 ${links.length}` : '—'}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Detail tabs ──────────────────────────────────────────────────────────────
function DetailsTab({ item, projectName, canWrite, onEdit, onStatus }) {
  const Row = ({ label, children }) => (
    <div style={{ display: 'flex', padding: '7px 0', borderBottom: '0.5px solid #f1efe8', fontSize: 12 }}>
      <div style={{ width: 130, color: '#888780', flexShrink: 0 }}>{label}</div>
      <div style={{ color: '#2c2c2a' }}>{children}</div>
    </div>
  );
  return (
    <div>
      {item.description && <div style={{ fontSize: 12.5, color: '#2c2c2a', lineHeight: 1.6, marginBottom: 12 }}>{item.description}</div>}
      <Row label="Type">{RAID_TYPES[item.type]?.label || item.type}</Row>
      <Row label="Scope">{projectName(item.project_id)}</Row>
      <Row label="Owner">{item.profiles?.full_name || '—'}</Row>
      <Row label="Priority">{item.priority}</Row>
      <Row label="Status">{(item.status || 'open').replace(/_/g, ' ')}</Row>
      <Row label="Due date">{item.due_date ? fmt(item.due_date) : '—'}</Row>
      <Row label="Raised">{fmt(item.created_at)}</Row>
      {item.type === 'dependency' && (
        <>
          <Row label="Dependency">{item.dependency_kind || '—'}</Row>
          {item.dependency_link && <Row label="Linked to">{item.dependency_link}</Row>}
        </>
      )}
      {canWrite && (
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="btn-ghost" onClick={onEdit}>Edit</button>
          {item.status === 'closed'
            ? <button className="btn-ghost" onClick={() => onStatus('open')}>Reopen</button>
            : <button className="btn-ghost" onClick={() => onStatus('closed')}>Close item</button>}
          {item.status !== 'escalated' && item.status !== 'closed' && (
            <button className="btn-ghost" style={{ color: '#A32D2D' }} onClick={() => onStatus('escalated')}>Escalate</button>
          )}
        </div>
      )}
    </div>
  );
}

function UpdatesTab({ updates, value, onChange, onAdd, canWrite }) {
  return (
    <div>
      {canWrite && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          <textarea className="form-textarea" rows={2} placeholder="Add an update…" value={value} onChange={(e) => onChange(e.target.value)} />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn-primary" onClick={onAdd} disabled={!value.trim()}>Add update</button>
          </div>
        </div>
      )}
      {updates.length === 0 ? <div className="empty">No updates yet.</div> : updates.map((u) => (
        <div key={u.id} style={{ padding: '8px 0', borderBottom: '0.5px solid #f1efe8' }}>
          <div style={{ fontSize: 12, color: '#2c2c2a', lineHeight: 1.55 }}>{u.body}</div>
          <div style={{ fontSize: 10, color: '#888780', marginTop: 3 }}>{u.profiles?.full_name || 'Someone'} · {fmt(u.created_at)}</div>
        </div>
      ))}
    </div>
  );
}

function LinksTab({ gateLinks, relatedIds, raidById, projectName, canWrite, onUnlinkGate, onUnlinkRelated, onOpenGatePicker, onOpenRelatedPicker, onOpenRelated, navigate, programmeId }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span className="ctitle">Linked gates</span>
          {canWrite && <button className="row-act" onClick={onOpenGatePicker}>+ Link gate</button>}
        </div>
        {gateLinks.length === 0 ? <div style={{ fontSize: 11, color: '#888780' }}>No gates linked.</div> : gateLinks.map((l) => (
          <div key={l.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', border: '0.5px solid #d3d1c7', borderRadius: 8, marginBottom: 5 }}>
            <div onClick={() => navigate(`/programme/${programmeId}/governance/stage-gates`)} style={{ cursor: 'pointer', minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#2c2c2a' }}>{l.stage_gates?.name || 'Gate'}</div>
              <div style={{ fontSize: 10, color: '#888780' }}>{projectName(l.stage_gates?.project_id)} · {(l.stage_gates?.status || '').replace(/_/g, ' ')}</div>
            </div>
            {canWrite && <button className="row-act row-act-close" onClick={() => onUnlinkGate(l.id)}>✕</button>}
          </div>
        ))}
      </div>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span className="ctitle">Related RAID items</span>
          {canWrite && <button className="row-act" onClick={onOpenRelatedPicker}>+ Link item</button>}
        </div>
        {relatedIds.length === 0 ? <div style={{ fontSize: 11, color: '#888780' }}>No related items.</div> : relatedIds.map((rid) => {
          const r = raidById[rid];
          if (!r) return null;
          return (
            <div key={rid} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', border: '0.5px solid #d3d1c7', borderRadius: 8, marginBottom: 5 }}>
              <div onClick={() => onOpenRelated(rid)} style={{ cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
                <TypeBadge type={r.type} short />
                <span style={{ fontSize: 12, color: '#2c2c2a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</span>
              </div>
              {canWrite && <button className="row-act row-act-close" onClick={() => onUnlinkRelated(rid)}>✕</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Form + picker ──────────────────────────────────────────────────────────────
function RaidForm({ form, setForm, projects, error }) {
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  return (
    <>
      <div className="form-2col">
        <div className="form-row">
          <label className="form-label">Type</label>
          <select className="form-select" value={form.type} onChange={(e) => set({ type: e.target.value })}>
            {ALL_TYPES.map((t) => <option key={t} value={t}>{RAID_TYPES[t].label}</option>)}
          </select>
        </div>
        <div className="form-row">
          <label className="form-label">Priority</label>
          <select className="form-select" value={form.priority} onChange={(e) => set({ priority: e.target.value })}>
            <option value="critical">Critical</option><option value="high">High</option>
            <option value="medium">Medium</option><option value="low">Low</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <label className="form-label">Title *</label>
        <input className="form-input" value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="Brief description of the item" />
      </div>
      <div className="form-row">
        <label className="form-label">Description</label>
        <textarea className="form-textarea" rows={3} value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Detail, context, or impact" />
      </div>
      <div className="form-2col">
        <div className="form-row">
          <label className="form-label">Owner</label>
          <OwnerSearch displayName={form.owner_name} onChange={(id, name) => set({ owner_id: id, owner_name: name })} />
        </div>
        <div className="form-row">
          <label className="form-label">Status</label>
          <select className="form-select" value={form.status} onChange={(e) => set({ status: e.target.value })}>
            <option value="open">Open</option><option value="in_progress">In progress</option>
            <option value="escalated">Escalated</option><option value="closed">Closed</option>
          </select>
        </div>
      </div>
      <div className="form-2col">
        <div className="form-row">
          <label className="form-label">Scope</label>
          <select className="form-select" value={form.project_id} onChange={(e) => set({ project_id: e.target.value })}>
            <option value="">Programme-level</option>
            {(projects || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="form-row">
          <label className="form-label">Due date</label>
          <input className="form-input" type="date" value={form.due_date} onChange={(e) => set({ due_date: e.target.value })} />
        </div>
      </div>
      {form.type === 'dependency' && (
        <div className="form-2col">
          <div className="form-row">
            <label className="form-label">Dependency type</label>
            <select className="form-select" value={form.dependency_kind} onChange={(e) => set({ dependency_kind: e.target.value })}>
              <option value="internal">Internal</option>
              <option value="external">External</option>
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Linked to</label>
            <input className="form-input" value={form.dependency_link} onChange={(e) => set({ dependency_link: e.target.value })} placeholder="System / team / third party" />
          </div>
        </div>
      )}
      {error && <div className="form-error" style={{ margin: 0 }}>{error}</div>}
    </>
  );
}

function PickerModal({ title, onClose, options, onPick, empty }) {
  const [q, setQ] = useState('');
  const filtered = options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()));
  return (
    <Modal title={title} onClose={onClose} footer={<button className="btn-ghost" onClick={onClose}>Close</button>}>
      <input className="form-input" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div style={{ marginTop: 10, maxHeight: 320, overflowY: 'auto' }}>
        {options.length === 0 ? <div className="empty">{empty}</div> : filtered.map((o) => (
          <div key={o.id} onClick={() => onPick(o.id)} style={{ padding: '9px 10px', borderRadius: 8, cursor: 'pointer', borderBottom: '0.5px solid #f1efe8' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f1efe8')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
            <div style={{ fontSize: 12, color: '#2c2c2a', fontWeight: 500 }}>{o.label}</div>
            {o.sub && <div style={{ fontSize: 10, color: '#888780', marginTop: 1 }}>{o.sub}</div>}
          </div>
        ))}
      </div>
    </Modal>
  );
}
